import { FC, useEffect, useState } from 'react'
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material'
import { EstadoDeLista } from '@/components/common/EstadoDeLista'
import { documentoService } from '@/services/documentoService'
import type { ConteudoPublicado } from '@/types/conteudo'
import { TIPOS_CONTEUDO } from '@/types/conteudo'
import { formatDate } from '@/utils/formatters'
import { tokens } from '@/theme'

interface Props {
  /** Material a exibir; `null` fecha o modal. */
  conteudo: ConteudoPublicado | null
  onClose: () => void
}

/**
 * "Visualização de material" (Figma, Produtos e Materiais — Onda 8 / A16).
 *
 * O PDF vem SEMPRE pelo endpoint auditado `GET /api/v1/documentos/{documentoId}/arquivo`
 * (`documentoService.baixarArquivo`), nunca por URL do storage — a peça publicada (A12)
 * só carrega o `documentoId`. O blob vira um object URL exibido em `<iframe>` e é
 * revogado ao fechar; "Baixar" reaproveita o mesmo blob.
 */
const MaterialDialog: FC<Props> = ({ conteudo, onClose }) => {
  const [url, setUrl] = useState<string | null>(null)
  const [blob, setBlob] = useState<Blob | null>(null)
  const [carregando, setCarregando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  const [tentativa, setTentativa] = useState(0)

  useEffect(() => {
    if (!conteudo) return
    let ativo = true
    let objectUrl: string | null = null
    setCarregando(true)
    setErro(null)
    setUrl(null)
    setBlob(null)
    documentoService
      .baixarArquivo(conteudo.documentoId)
      .then((arquivo) => {
        if (!ativo) return
        objectUrl = URL.createObjectURL(arquivo)
        setBlob(arquivo)
        setUrl(objectUrl)
      })
      .catch((e: unknown) => {
        if (!ativo) return
        setErro(e instanceof Error ? e.message : 'Falha ao carregar o material')
      })
      .finally(() => {
        if (ativo) setCarregando(false)
      })
    return () => {
      ativo = false
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [conteudo, tentativa])

  const baixar = () => {
    if (!conteudo || !blob) return
    const href = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = href
    link.download = `${conteudo.titulo} v${conteudo.versao}.pdf`
    link.click()
    URL.revokeObjectURL(href)
  }

  return (
    <Dialog open={!!conteudo} onClose={onClose} maxWidth="lg" fullWidth aria-labelledby="material-dialog-titulo">
      <DialogTitle id="material-dialog-titulo">
        <Typography variant="overline" sx={{ color: tokens.textSecondary, display: 'block' }}>
          Visualização de material
        </Typography>
        {conteudo?.titulo}
      </DialogTitle>
      <DialogContent dividers>
        {conteudo && (
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
            <Chip size="small" label={TIPOS_CONTEUDO[conteudo.tipo]} />
            <Chip size="small" variant="outlined" label={`Versão ${conteudo.versao}`} />
            {conteudo.produtoNome && <Chip size="small" variant="outlined" label={conteudo.produtoNome} />}
            <Typography variant="caption" sx={{ color: tokens.textSecondary }}>
              Publicado em {formatDate(conteudo.publicadoEm)}
              {conteudo.vigenciaFim ? ` · vigente até ${formatDate(conteudo.vigenciaFim)}` : ''}
            </Typography>
          </Stack>
        )}

        {carregando ? (
          <EstadoDeLista estado="carregando" mensagem="Carregando material…" minHeight={320} />
        ) : erro ? (
          <EstadoDeLista
            estado="erro"
            mensagem="Não foi possível carregar o material"
            detalhe={erro}
            acao={{ label: 'Tentar novamente', onClick: () => setTentativa((t) => t + 1) }}
            minHeight={320}
          />
        ) : url ? (
          <Box
            component="iframe"
            title={`Material: ${conteudo?.titulo ?? ''}`}
            src={url}
            sx={{ width: '100%', height: '70vh', border: 0, borderRadius: 1, bgcolor: tokens.divider }}
          />
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Fechar</Button>
        <Button variant="contained" onClick={baixar} disabled={!blob}>
          Baixar PDF
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default MaterialDialog
